import { FormProvider, useForm } from "react-hook-form";
import { SignInParams, useAuth } from "~/presentation/core/contexts/AuthContext";
import { useEffect, useState } from "react";
import { styled } from "~/application/theme";
import { BiztripLogo } from "~/components/BiztripLogo";
import { Form } from "~/components/Form";
import { Flex } from "~/components/Flex";
import { Text } from "~/components/Text";
import { SignInForm } from "./components/SignInForm";
import { TwoFactorAuthForm } from "./components/TwoFactorAuthForm";
import useMobile from "~/presentation/shared/hooks/useMobile";

const BackgroundVideo = styled("video", {
  position: "absolute",
  top: 0,
  left: 0,
  height: "50vh",
  width: "160%",
  objectFit: "cover",
  zIndex: 0,
  "@lg": {
    height: "110vh",
    width: "115vw",
    objectFit: "unset",
  },
});

export interface SignInContainerProps {
  onSignIn: (data: SignInParams, onErrorAttempt?: () => void) => Promise<void>;
}

export const SignInContainer = ({ onSignIn }: SignInContainerProps) => {
  const { attempts, qrCodeUrl } = useAuth();
  const MAX_ATTEMPTS = 5;
  const isMobile = useMobile();

  const methods = useForm<SignInParams>({
    defaultValues: {
      email: "",
      password: "",
      securityKey: "",
      keepSigned: true,
    },
  });

  const { handleSubmit } = methods;

  const [showAttempts, setShowAttempts] = useState(false);

  const handleSignIn = async (data: SignInParams) => {
    await onSignIn(data, () => {
      setShowAttempts(true);
    });
  };

  useEffect(() => {
    if (attempts > 0 && attempts < MAX_ATTEMPTS) {
      return setShowAttempts(true);
    }

    setShowAttempts(false);
  }, [attempts]);

  useEffect(() => {
    setShowAttempts(false);
  }, []);

  return (
    <Flex
      align={{ "@initial": "start", "@lg": "center" }}
      justify={{ "@initial": "center", "@lg": "around" }}
      css={{
        backgroundColor: "$primary-600",
        height: "100vh",
        overflow: "hidden",
        position: "relative",
        width: "100%",
      }}
    >
      <BackgroundVideo
        src="/Corporate_credit_card_close-up_202607221529.mp4"
        autoPlay
        loop
        muted
        playsInline
      />
      <Flex></Flex>

      <Flex
        direction="column"
        align="center"
        css={{
          position: "relative",
          justifySelf: "end",
        }}
      >
        {!isMobile && (
          <Flex
            justify="center"
            css={{
              position: "absolute",
              top: "-2.1875rem",
              backgroundColor: "#002F6C",
              color: "$neutrals-white",
              padding: "$3 $4 $6 $4",
              borderRadius: "$mdlg $mdlg 0 0",
              fontSize: "$xs",
              fontWeight: 600,
              width: "90%",
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={() => window.open("https://bizpay.biztrip.com.br/cartoes", "_blank")}
          >
            Conheça mais sobre o{" "}
            <Text as="span" fw="700" css={{ ml: "$1" }}>
              Bizpay
            </Text>{" "}
            <Text as="span" css={{ ml: "$1" }}>
              ↗
            </Text>
          </Flex>
        )}

        <Flex
          direction="column"
          align="center"
          css={{
            backgroundColor: "$neutrals-white",
            padding: "$10 $6 max($6, env(safe-area-inset-bottom)) $6",
            borderRadius: "2rem 2rem 0 0",
            width: "100vw",
            minHeight: "65vh",
            marginTop: "40vh",
            boxShadow: "0 0.625rem 1.5625rem rgba(0, 0, 0, 0.2)",
            zIndex: 3,
            "@lg": {
              padding: "$14 $8",
              borderRadius: "$rounded",
              width: "auto",
              minWidth: "25rem",
              minHeight: "auto",
              marginTop: 0,
            },
          }}
        >
          <BiztripLogo size="lg" />

          <Flex direction="column" align="center" gap="1" css={{ mt: "$8", mb: "$6" }}>
            <Text fw="600" size="5" variant="neutral-900">
              Acesse sua conta
            </Text>
            <Text size="2" variant="neutral-600">
              Informe seu e-mail e senha para entrar
            </Text>
          </Flex>

          <Form onSubmit={handleSubmit(handleSignIn)} css={{ width: "100%", gap: "$6" }}>
            <FormProvider {...methods}>
              {qrCodeUrl === undefined ? (
                <SignInForm showAttempts={showAttempts} />
              ) : (
                <TwoFactorAuthForm />
              )}
            </FormProvider>
          </Form>
        </Flex>
      </Flex>
    </Flex>
  );
};
